/**
 * Job-Based Communication & Routing System
 * 
 * Defines structured message types for internal job communication,
 * with routing to specific participants and read/response tracking.
 */

// ── Message Status ──
export type JobMessageStatus = "unread" | "read" | "responded";

// ── Sender Role ──
export type SenderRole = "reviewer" | "sales" | "ops" | "warehouse" | "installer" | "manager" | "system";

/** Map any raw role string to a valid SenderRole for the database check constraint. */
export function normalizeSenderRole(role: string | null | undefined): SenderRole {
  if (!role) return "system";
  const normalized = role.trim().toLowerCase().replace(/[\s-]+/g, "_");
  switch (normalized) {
    case "sales":
    case "sales_rep":
    case "salesperson":
    case "call_center":
    case "callcenter":
      return "sales";
    case "ops":
    case "ops_manager":
    case "operations":
    case "operations_manager":
      return "ops";
    case "warehouse":
    case "warehouse_manager":
      return "warehouse";
    case "installer":
    case "field_crew":
    case "subcontractor":
    case "subcontractors":
    case "head_of_installation":
      return "installer";
    case "reviewer":
    case "review":
      return "reviewer";
    case "manager":
    case "sales_manager":
      return "manager";
    default:
      return "system";
  }
}

// ── Message Priority ──
export type MessagePriority = "normal" | "high" | "urgent";

// ── Message Category (tabs in the chat UI) ──
export type MessageCategory = "orders" | "concerns" | "general";

// ── Simple profile for @mention autocomplete ──
export interface ProfileSimple {
  id: string;
  full_name?: string | null;
  email?: string | null;
  role?: string | null;
  avatar_url?: string | null;
}

// ── Job Participants ──
export interface JobParticipants {
  salesRepId: string;
  salesManagerId?: string;
  reviewerId?: string;
  opsManagerId?: string;
  warehouseManagerId?: string;
  installerId?: string;
}

// Shape of Job that participant resolution needs — avoids importing the full Job type
// (which would create a circular dependency from data types → mappers).
export interface JobParticipantInput {
  salesRep?: string;
  assignedCrew?: string;
  assignedSubcontractorId?: string;
  assignedSubcontractor?: string;
}

// ── Job Message ──
export interface JobMessage {
  id: string;
  jobId: string;
  /** Message content (HTML from Tiptap editor) */
  body: string;
  /** Tab category: orders, concerns, or general */
  category: MessageCategory;
  /** Mention user IDs extracted from the body */
  mentionUserIds?: string[];
  /** Who sent it */
  senderId: string;
  senderName: string;
  senderRole: SenderRole;
  /** When */
  timestamp: string;
  /** Priority */
  priority: MessagePriority;
  /** Read status per recipient: recipientId → status */
  recipientStatuses: Record<string, JobMessageStatus>;
  /** IDs of users this message is routed to */
  recipientIds: string[];
  /** Optional context */
  context?: string; // e.g. "Build Review", "Material Planning", "Warehouse"
  /** Optional reference to a review event */
  relatedEventId?: string;
}

/** Create a new job message with routing */
export function createJobMessage(
  jobId: string,
  body: string,
  sender: { id: string; name: string; role: SenderRole },
  recipientIds: string[],
  options?: {
    priority?: MessagePriority;
    context?: string;
    relatedEventId?: string;
    category?: MessageCategory;
    mentionUserIds?: string[];
  }
): JobMessage {
  const recipientStatuses: Record<string, JobMessageStatus> = {};
  recipientIds.forEach((rid) => {
    recipientStatuses[rid] = "unread";
  });

  return {
    id: `jm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    jobId,
    body,
    senderId: sender.id,
    senderName: sender.name,
    senderRole: sender.role,
    timestamp: new Date().toISOString(),
    priority: options?.priority ?? "normal",
    recipientStatuses,
    recipientIds,
    context: options?.context,
    relatedEventId: options?.relatedEventId,
    category: options?.category ?? "general",
    mentionUserIds: options?.mentionUserIds,
  };
}

/** Get unread messages for a specific user */
export function getUnreadMessagesForUser(messages: JobMessage[], userId: string): JobMessage[] {
  return messages.filter(
    (m) => m.recipientStatuses[userId] === "unread"
  );
}

/** Get messages needing attention (unread, not from this user) */
export function getMessagesNeedingAttention(messages: JobMessage[], userId: string): JobMessage[] {
  return messages.filter(
    (m) => m.senderId !== userId && m.recipientStatuses[userId] === "unread"
  );
}

/** Mark a message as read for a user */
export function markMessageRead(message: JobMessage, userId: string): JobMessage {
  return {
    ...message,
    recipientStatuses: {
      ...message.recipientStatuses,
      [userId]: message.recipientStatuses[userId] === "unread" ? "read" : message.recipientStatuses[userId],
    },
  };
}

/** Mark a message as responded for a user */
export function markMessageResponded(message: JobMessage, userId: string): JobMessage {
  return {
    ...message,
    recipientStatuses: {
      ...message.recipientStatuses,
      [userId]: "responded",
    },
  };
}

/** Default participant mapping from Job fields */
export function deriveJobParticipants(job: { salesRep: string; assignedCrew?: string; assignedSubcontractorId?: string; assignedSubcontractor?: string }): JobParticipants {
  const installerId = job.assignedSubcontractorId && job.assignedSubcontractorId.trim().length > 0
    ? job.assignedSubcontractorId
    : (job.assignedCrew && job.assignedCrew.trim().length > 0 ? job.assignedCrew : undefined);
  return {
    salesRepId: job.salesRep || "sales-rep",
    salesManagerId: "sales-manager",
    reviewerId: "reviewer",
    opsManagerId: "ops-manager",
    warehouseManagerId: "warehouse-manager",
    installerId,
  };
}

/** Collect all non-empty participant IDs from a job, excluding the sender. */
export function getJobParticipantIds(job: { salesRep: string; assignedCrew?: string; assignedSubcontractorId?: string; assignedSubcontractor?: string }, excludeId?: string): string[] {
  const participants = deriveJobParticipants(job);
  const allIds = [
    participants.salesRepId,
    participants.salesManagerId,
    participants.reviewerId,
    participants.opsManagerId,
    participants.warehouseManagerId,
    participants.installerId,
  ].filter(Boolean) as string[];
  if (!excludeId) return allIds;
  return allIds.filter((id) => id !== excludeId);
}
